import { EventEmitter, Injectable } from '@angular/core';
import { ConfigService } from './config.service';

export interface LocalStorageChangedEvent {
    key: string;
    oldValue: any;
    newValue: any;
}

@Injectable()
export class LocalStorageService {
    storageChanged = new EventEmitter<LocalStorageChangedEvent>();
    private _localStorage: any = null;

    constructor(private _configService: ConfigService) {
        if (window && this._configService.config?.cacheSettings!.enableCache) {
            this._localStorage = window.localStorage;
            this.init();
            window.addEventListener('storage', this._listener);
        }
    }

    private init() {
        if (this._localStorage) {
            if (!this.get('cacheClearDate') || Date.parse(this._configService.config.cacheSettings.expireCacheDate) >= this.get('cacheClearDate')) {
                this.clear();
                this.set('cacheClearDate', Date.now());
            }
        }
    }

    get(key: string): any {
        if (this._localStorage) {
            let jsonValue = this._localStorage.getItem(key);
            return this._fromLocalStorageValue(jsonValue);
        }
        return undefined;
    }

    getRaw(key: string): any {
        if (this._localStorage) {
            return this._localStorage.getItem(key);
        }
        return undefined;
    }

    set(key: string, value: any) {
        if (this._localStorage) {
            let jsonValue = this._toLocalStorageValue(value);
            return this._localStorage.setItem(key, jsonValue);
        }
    }

    setRaw(key: string, value: any) {
        if (this._localStorage) {
            return this._localStorage.setItem(key, value);
        }
    }

    remove(key: string): any {
        if (this._localStorage) {
            return this._localStorage.removeItem(key);
        }
    }

    clear() {
        if (this._localStorage) {
            return this._localStorage.clear();
        }
    }

    private _toLocalStorageValue(value: any): string {
        return JSON.stringify(value);
    }

    private _fromLocalStorageValue(value: string): any {
        if (value) {
            try {
                return JSON.parse(value);
            } catch (e) {
                return undefined;
            }
        }
        return undefined;
    }

    private _listener = (event: StorageEvent) => {
        let e: LocalStorageChangedEvent = {
            key: event.key ?? '',
            oldValue: this._fromLocalStorageValue(event.oldValue ?? ''),
            newValue: this._fromLocalStorageValue(event.newValue ?? '')
        };
        this.storageChanged.emit(e);
    }
}
